import React, {useRef, useState, useCallback} from "react"
import {OutsideClick} from "../../hooks/OutsideClick"
import {UseStores} from "../../hooks/UseStores"
import {debounce, URLPATHS} from "../../utils/utils"
import {Spinner} from "../Spinner"
import InternalError from "../../components/InternalError"
import NoResults from "../../components/NoResults"
import Highlight from "react-highlight-words"
import "./index.scss"
import {navigate} from "@reach/router"
import {useManualQuery} from "graphql-hooks"
import {SEARCH_QUERY} from "../../gql/gqlBrands"
import {defaultGeo, removeTrailingLeadingChars, defaultMapGeos} from "../../utils/utils"

const SearchInput = (props) => {
  const {disStore, locGroupStore, poiStore, gtMapStore, navStore, backdropStore} =
    UseStores()

  let searchQuery = SEARCH_QUERY

  const [searchManual, {loading: searchLoading, data, error}] =
    useManualQuery(searchQuery)

  const ref = useRef(null) //outside reference
  const inputRef = useRef(null)
  const [openSearch, setOpenSearch] = useState(false)
  const [inputVal, setInputVal] = useState("")

  const handleSearch = (ref) => {
    //pass ref for true input value - if passing actual string
    //then the first char will remain present and a query call is triggered
    //if user deletes the input quickly
    let trimmedText = ref.current.value.trim() //remove space around string

    //call only on input of 1 chars or more
    if (trimmedText.length >= 1) {
      setOpenSearch(true)
      searchManual({variables: {text: trimmedText}})
    }
  }

  const debounceHandleSearch = useCallback(debounce(handleSearch, 400), [])

  const handleOnChange = (ref) => {
    let inputText = ref.current.value
    setInputVal(inputText)
    if (inputText) {
      debounceHandleSearch(ref)
    } else {
      setOpenSearch(false)
    }
  }

  const handleClearInput = () => {
    setOpenSearch(false)
    setInputVal("")
  }

  OutsideClick(ref, () => {
    handleClearInput()
  })

  const handleOnClick = async (groupName, id, detail) => {
    navStore.setPreviousPage(window.location.pathname) // For POI map. If the user lands on insights directly, we need to show Discovery map first.
    if (backdropStore.isOpen) backdropStore.setIsOpen(false)

    if (groupName === "brands" || groupName === "categories") {
      navigate("/brands/discovery")
    } else if (groupName === "locationGroups") {
      navigate("/locationgroups")
    } else {
      poiStore.setBusinessName(detail.name)
      poiStore.setPoiData(detail)
      poiStore.setPoiId(detail.id)
      navigate(`/poi?id=${detail.id}`)
    }

    setOpenSearch(false)
    setInputVal("")

    // has to wait after the newly navigated page done rendering (appData, store, table)
    setTimeout(async () => {
      if (groupName === "brands") {
        disStore.setBrandCat(id, null)
        disStore.setGeoTimeObj({geo: defaultGeo})
        disStore.setGeoServiceObj({selected: id}) //trigger GEO SERVICE REQUEST
      } else if (groupName === "categories") {
        disStore.setBrandCat(null, id)
        disStore.setCatSearchBool()
        disStore.setGeoTimeObj({geo: defaultGeo})
        disStore.setGeoServiceObj({selected: id}) //trigger GEO SERVICE REQUEST
      } else if (groupName === "locationGroups") {
        locGroupStore.setLocGeoTime({geo: defaultGeo})
        locGroupStore.setIsLGSearched(true)
        locGroupStore.setTagId(id)
        locGroupStore.setGeoServiceObj({selected: id}) //trigger GEO SERVICE REQUEST
      } else {
        // This a temporary quick fix for rendering POI map correctly if the user starts the app from Insights page.
        // This will be removed after the fix is made in the map.

        if (navStore.previousPage === URLPATHS.INSIGHTS) {
          gtMapStore.setMapObj({
            geo: {geoType: "COUNTRY", geoId: 0},
            categoryId: disStore.sicCode,
            brandId: disStore.brandId,
            visitDuration: disStore.timePeriod,
            mapGeos: defaultMapGeos,
            showLoader: true,
            locationGroupId: null,
          })

          setTimeout(() => {
            gtMapStore.setMapObj({
              geo: {
                geoType: "POI",
                geoId: detail.id,
                lat: detail.latitude,
                lon: detail.longitude,
              },
              categoryId: null,
              brandId: null,
              mapGeos: defaultMapGeos,
              showLoader: false,
              locationGroupId: null,
              mapPadding: {left: 0},
            })
          }, 2000)
        } else {
          gtMapStore.setMapObj({
            geo: {
              geoType: "POI",
              geoId: detail.id,
              lat: detail.latitude,
              lon: detail.longitude,
            },
            categoryId: null,
            brandId: null,
            mapGeos: defaultMapGeos,
            showLoader: false,
            locationGroupId: null,
            mapPadding: {left: 0},
          })
        }
      }
    }, 10)
  }

  const groupNameMap = new Map([
    ["brands", "Brands"],
    ["categories", "Categories"],
    ["locationGroups", "Location Groups"],
    ["pois", "POI"],
  ])

  const getSpanNameFromGroup = (group) => {
    for (let [key, value] of groupNameMap.entries()) {
      if (key === group) return value
    }
  }

  const getSearchResultItem = (group, detail) => {
    let searchResultItem = ""
    if (group === "pois") {
      let poiObject = poiStore.generateAddressAndStatus(detail)
      searchResultItem = `${poiObject.name}, ${poiObject.address}`
    } else {
      searchResultItem = detail.name
    }
    return searchResultItem
  }

  const classRender = openSearch ? "show-dropdown" : "hide-dropdown"

  const searchResults =
    data &&
    (data.search.brands.length !== 0 ||
      data.search.categories.length !== 0 ||
      data.search.locationGroups.length !== 0 ||
      data.search.pois.length !== 0) ? (
      Object.keys(data.search).map((group) => {
        if (data.search[group].length !== 0) {
          //if search group has data
          return (
            <div
              className="search-matched-list"
              data-testid="matched-list-test"
              key={group}
            >
              <div className="search-group">
                <ul>
                  <li className="search-group-header" key={group}>
                    <span className="label">{getSpanNameFromGroup(group)}</span>
                    <span className="divider"></span>
                  </li>

                  {data.search[group].map((detail, i) => {
                    let id
                    if (group === "brands" || group === "locationGroups") {
                      id = detail.id
                    } else {
                      id = detail.sicCode
                    }

                    return (
                      <li
                        key={i}
                        className="search-list-row"
                        onClick={() => handleOnClick(group, id, detail)}
                      >
                        <Highlight
                          highlightClassName="highlightMatch"
                          searchWords={[removeTrailingLeadingChars(inputVal)]}
                          autoEscape={true}
                          textToHighlight={getSearchResultItem(group, detail)} //display search result here
                          role="search-onclick-selection-test"
                        />
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>
          )
        }
      })
    ) : (
      <NoResults />
    ) //no data from search query

  const searchContent = searchLoading ? (
    <Spinner size="medium" />
  ) : error ? (
    <InternalError />
  ) : (
    searchResults
  )

  return (
    <>
      <div className="search-bar" ref={ref} data-testid="search-container-test">
        <div className="search-input-wrapper">
          <input
            className="search-field"
            placeholder={props.placeholder || "Search"}
            onChange={() => handleOnChange(inputRef)}
            value={inputVal}
            aria-label="search-input"
            name="search-input"
            ref={inputRef}
          />
          {inputVal && <div className="icon clear-icon" onClick={handleClearInput} />}
          <div className="icon search-icon" />
        </div>
        <div className={`search-list-container ${classRender}`}>{searchContent}</div>
      </div>
    </>
  )
}
export default SearchInput
